import {PayloadAction, createSlice} from '@reduxjs/toolkit';
import {Role} from 'src/openapi/admin/models/Role';
import {SearchDirection} from 'src/openapi/admin/models/SearchDirection';
import {UserSortField} from 'src/openapi/admin/models/UserSortField';
import {Vendor} from 'src/openapi/admin/models/Vendor';

import type {Error} from 'src/openapi/admin/models/Error';
import type {Pagination} from 'src/openapi/admin/models/Pagination';
import type {SortCriteria} from 'src/openapi/admin/models/SortCriteria';
import type {User} from 'src/openapi/admin/models/User';
import type {UserSearchCriteria} from 'src/openapi/admin/models/UserSearchCriteria';
import type {AppState} from 'src/store';

const DEFAULT_PAGINATION: Pagination = {page: 1, size: 25};

type InitialState = {
    items: User[],
    searchCriteria: UserSearchCriteria,
    sortCriteria: SortCriteria,
    pagination: Pagination,
    isLoading: boolean,
    serverError: Error | null,
    totalCount: number,
    roles: Role[],
    vendors: Vendor[],
    rolesLoading: boolean,
    vendorsLoading: boolean,
};

export const accessManagementUsersSlice = createSlice({
    name: 'accessManagementUsers',
    initialState: {
        items: [],
        searchCriteria: {},
        sortCriteria: {
            sortField: UserSortField.EMAIL,
            sortDirection: SearchDirection.ASC,
        },
        pagination: DEFAULT_PAGINATION,
        isLoading: false,
        serverError: null,
        totalCount: 0,
        pbAdminAccounts: [],
        roles: [],
        vendors: [],
        rolesLoading: false,
        vendorsLoading: false,
    } as InitialState,
    reducers: {
        clear: state => {
            state.items = [];
            state.searchCriteria = {};
            state.sortCriteria = {
                sortField: UserSortField.EMAIL,
                sortDirection: SearchDirection.ASC,
            };
            state.pagination = DEFAULT_PAGINATION;
            state.isLoading = false;
            state.serverError = null;
            state.totalCount = 0;
        },
        changeSortCriteria: (state, action: PayloadAction<SortCriteria>) => {
            state.sortCriteria = action.payload;
        },
        changePage: (state, action: PayloadAction<number>) => {
            state.pagination.page = action.payload;
        },
        changePageSize: (state, action: PayloadAction<number>) => {
            state.pagination.size = action.payload;
            state.pagination.page = 1;
        },
        changeEmail: (state, action: PayloadAction<string>) => {
            if (action.payload) {
                state.searchCriteria.email = action.payload;
            } else {
                delete state.searchCriteria.email;
            }
            state.pagination.page = 1;
        },
        changeRole: (state, action: PayloadAction<string | undefined>) => {
            if (action.payload) {
                state.searchCriteria.roles = [action.payload];
            } else {
                delete state.searchCriteria.roles;
            }
            state.pagination.page = 1;
        },
        changeVendor: (state, action: PayloadAction<string | undefined>) => {
            if (action.payload) {
                state.searchCriteria.vendors = [action.payload];
            } else {
                delete state.searchCriteria.vendors;
            }
            state.pagination.page = 1;
        },
        changeTool: (state, action: PayloadAction<string | undefined>) => {
            if (action.payload) {
                state.searchCriteria.tools = [action.payload];
            } else {
                delete state.searchCriteria.tools;
            }
            state.pagination.page = 1;
        },
        setTotalCount: (state, action: PayloadAction<number>) => {
            state.totalCount = action.payload;
        },
        setUserItems: (state, action: PayloadAction<User[]>) => {
            state.items = action.payload;
        },
        setLoading: (state, action: PayloadAction<boolean>) => {
            state.isLoading = action.payload;
        },
        setServerError: (state, action: PayloadAction<Error | null>) => {
            state.serverError = action.payload;
        },
        setUsersData: (state, action: PayloadAction<{users: User[], totalCount: number, loading: boolean}>) => {
            state.items = action.payload.users;
            state.totalCount = action.payload.totalCount;
            state.isLoading = action.payload.loading;
        },
        setRoles: (state, action: PayloadAction<Role[]>) => {
            state.roles = action.payload;
            state.rolesLoading = false;
        },
        setVendors: (state, action: PayloadAction<Vendor[]>) => {
            state.vendors = action.payload;
            state.vendorsLoading = false;
        },
        setRolesLoading: (state, action: PayloadAction<boolean>) => {
            state.rolesLoading = action.payload;
        },
        setVendorsLoading: (state, action: PayloadAction<boolean>) => {
            state.vendorsLoading = action.payload;
        },
    },
});

export const {
    clear,
    changeSortCriteria,
    changePage,
    changePageSize,
    changeEmail,
    changeRole,
    changeVendor,
    changeTool,
    setTotalCount,
    setUserItems,
    setLoading,
    setServerError,
    setUsersData,
    setRoles,
    setVendors,
    setRolesLoading,
    setVendorsLoading,
} = accessManagementUsersSlice.actions;

export const accessManagementUsersReducer = accessManagementUsersSlice.reducer;

export const accessManagementUsersItemsSelector = (state: AppState) =>
    state.accessManagementUsers.items;

export const accessManagementUsersIsLoadingSelector = (state: AppState) =>
    state.accessManagementUsers.isLoading;

export const accessManagementUsersTotalCountSelector = (state: AppState) =>
    state.accessManagementUsers.totalCount;

export const accessManagementUsersServerErrorSelector = (state: AppState) =>
    state.accessManagementUsers.serverError;

export const accessManagementUsersSearchCriteriaSelector = (state: AppState) =>
    state.accessManagementUsers.searchCriteria;

export const accessManagementUsersSortCriteriaSelector = (state: AppState) =>
    state.accessManagementUsers.sortCriteria;

export const accessManagementUsersPaginationSelector = (state: AppState) =>
    state.accessManagementUsers.pagination;

export const accessManagementUsersEmailFieldSelector = (state: AppState) =>
    state.accessManagementUsers.searchCriteria.email;

export const accessManagementUsersRoleFieldSelector = (state: AppState) =>
    state.accessManagementUsers.searchCriteria.roles?.[0];

export const accessManagementUsersVendorFieldSelector = (state: AppState) =>
    state.accessManagementUsers.searchCriteria.vendors?.[0];

export const accessManagementUsersToolFieldSelector = (state: AppState) =>
    state.accessManagementUsers.searchCriteria.tools?.[0];

export const accessManagementUsersRolesSelector = (state: AppState) =>
    state.accessManagementUsers.roles;

export const accessManagementUsersVendorsSelector = (state: AppState) =>
    state.accessManagementUsers.vendors;

export const accessManagementUsersRolesLoadingSelector = (state: AppState) =>
    state.accessManagementUsers.rolesLoading;

export const accessManagementUsersVendorsLoadingSelector = (state: AppState) =>
    state.accessManagementUsers.vendorsLoading;
